import { AssistantService } from '../api/AssistantService';
import { RequestFlag } from '../api/protosCompiled/assistants/assistants_pb';

// Simple markdown to HTML converter for better formatting
const convertMarkdownToHtml = (markdown: string): string => {
  let html = markdown;

  // First, escape special characters and handle backslashes
  html = html
    .replace(/\\"/g, '"') // Replace escaped quotes
    .replace(/\\'/g, "'") // Replace escaped single quotes
    .replace(/\\n/g, '\n') // Replace escaped newlines
    .replace(/\\t/g, '\t') // Replace escaped tabs
    .replace(/\\{2,}/g, '\\') // Replace multiple backslashes with single
    .replace(/\\(?![\\"])/g, ''); // Remove single backslashes unless they're escaping another backslash or quote

  // Add container wrapper with max-width and centered layout
  const wrapContent = (content: string) => `
    <div style="max-width: 1200px; margin: 0 auto; padding: 0 24px;">
      ${content}
    </div>
  `;

  // Normalize line endings and remove extra blank lines
  html = html.replace(/\r\n/g, '\n');
  html = html.replace(/\n{3,}/g, '\n\n');

  // Convert headers with improved styling
  html = html.replace(
    /^### (.*$)/gm,
    '<h3 style="color: #1976d2; margin: 12px 0 4px; font-size: 1.5em; font-weight: 600;">$1</h3>'
  );
  html = html.replace(
    /^## (.*$)/gm,
    '<h2 style="color: #1976d2; margin: 16px 0 6px; font-size: 1.75em; font-weight: 600;">$1</h2>'
  );
  html = html.replace(
    /^# (.*$)/gm,
    '<h1 style="color: #1976d2; margin: 20px 0 8px; font-size: 2em; font-weight: 600;">$1</h1>'
  );

  // Convert bold text with better contrast
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');

  // Convert italic text
  html = html.replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>');

  // Convert bullet points to list items with proper spacing
  const lines = html.split('\n');
  let inList = false;
  const processedLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    if (line.match(/^- /)) {
      if (!inList) {
        processedLines.push(
          '<ul style="margin: 4px 0; padding-left: 24px; list-style-type: disc;">'
        );
        inList = true;
      }
      processedLines.push(
        '<li style="margin: 2px 0; line-height: 1.4;">' + line.replace(/^- /, '') + '</li>'
      );
    } else {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(line);
    }
  }

  if (inList) {
    processedLines.push('</ul>');
  }

  html = processedLines.join('\n');

  // Convert line breaks to paragraphs with minimal spacing
  html = html.replace(/\n\n/g, '</p><p style="margin: 4px 0; line-height: 1.4;">');
  html = '<p style="margin: 4px 0; line-height: 1.4;">' + html + '</p>';

  // Clean up empty paragraphs and normalize spacing
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/>\s+</g, '><'); // Remove whitespace between tags

  // Convert links with better styling
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none; border-bottom: 1px solid #1976d2;">$1</a>'
  );

  // Handle warning symbols and special formatting with improved visibility
  html = html.replace(
    /⚠️/g,
    '<span style="color: #ff9800; font-weight: 500; padding: 0 4px;">⚠️</span>'
  );
  html = html.replace(
    /💰/g,
    '<span style="color: #4caf50; font-weight: 500; padding: 0 4px;">💰</span>'
  );
  html = html.replace(
    /📊/g,
    '<span style="color: #2196f3; font-weight: 500; padding: 0 4px;">📊</span>'
  );
  html = html.replace(
    /🔍/g,
    '<span style="color: #9c27b0; font-weight: 500; padding: 0 4px;">🔍</span>'
  );

  // Add sections for better organization with minimal spacing
  html = html.replace(
    /(^|\n)(.*?:)(\n|$)/g,
    '$1<div style="margin: 8px 0 4px;"><h4 style="color: #666; margin: 0 0 2px; font-size: 1.1em; font-weight: 500;">$2</h4>$3'
  );

  // Wrap the entire content
  return wrapContent(html);
};

export interface StreamingReportOptions {
  projectId: string;
  estimateId: string;
  estimateName?: string;
  onChunk: (chunk: string) => void;
  onComplete?: (fullContent: string) => void;
  onError?: (error: Error) => void;
  shouldPersistOnUnmount?: boolean;
}

export class ReportGenerationService {
  private static getEstimateReportPrompt(estimateName: string): string {
    return `Please provide an estimate report for ${estimateName}.`;
  }

  private static getItemAnalysisPrompt(): string {
    return `Analyze Extended Cost Items`;
  }

  private static async persistReport(estimateId: string, content: string): Promise<void> {
    const { useAnalysisReportStore } = await import('../stores/analysisReportStore');
    const store = useAnalysisReportStore.getState();
    store.addReport(estimateId, content);
  }

  /**
   * Generate an estimate report with streaming content
   */
  public static async generateEstimateReport(options: StreamingReportOptions): Promise<void> {
    const {
      projectId,
      estimateId,
      estimateName = '',
      onChunk,
      onComplete,
      onError,
      shouldPersistOnUnmount,
    } = options;

    const prompt = this.getEstimateReportPrompt(estimateName);
    let fullResponse = '';

    try {
      await AssistantService.streamMessage({
        usermessage: prompt,
        projectid: projectId,
        estimateid: estimateId,
        requestflag: RequestFlag.REQUEST_FLAG_ESTIMATE_REPORT,
        onMessage: (chunk: string) => {
          fullResponse += chunk;
          // Always convert the full accumulated content to HTML and send it
          const htmlContent = convertMarkdownToHtml(fullResponse);
          onChunk(htmlContent);
        },
      });

      const htmlResponse = convertMarkdownToHtml(fullResponse);

      // Do NOT persist estimate reports - only item analysis reports should be cached
      // This report is for general estimate information and doesn't need caching or cooldowns

      if (onComplete) {
        // Final content is the same as what was last sent via onChunk
        onComplete(htmlResponse);
      }
    } catch (error) {
      console.error('Error generating estimate report:', error);
      if (onError) {
        onError(error as Error);
      }
    }
  }

  /**
   * Generate an item analysis report with streaming content
   */
  public static async generateItemAnalysisReport(options: StreamingReportOptions): Promise<void> {
    const { projectId, estimateId, onChunk, onComplete, onError, shouldPersistOnUnmount } = options;

    const prompt = this.getItemAnalysisPrompt();
    let fullResponse = '';

    try {
      await AssistantService.streamMessage({
        usermessage: prompt,
        projectid: projectId,
        estimateid: estimateId,
        requestflag: RequestFlag.REQUEST_FLAG_ITEM_ANALYSIS,
        onMessage: (chunk: string) => {
          fullResponse += chunk;
          // Always convert the full accumulated content to HTML and send it
          const htmlContent = convertMarkdownToHtml(fullResponse);
          onChunk(htmlContent);
        },
      });

      const htmlResponse = convertMarkdownToHtml(fullResponse);

      // Always persist item analysis reports, regardless of UI state
      await this.persistReport(estimateId, htmlResponse);

      if (onComplete) {
        // Final content is the same as what was last sent via onChunk
        onComplete(htmlResponse);
      }
    } catch (error) {
      console.error('Error generating item analysis report:', error);
      if (onError) {
        onError(error as Error);
      }
    }
  }
}
